(function () {
  const STORAGE_KEY = 'comparison_v1';
  const MAX_ITEMS = 4;
  let cachedProducts = [];
  let dropdownState = {};
  let barOpen = false;

  // ----------------------------------------------------------------
  // State
  // ----------------------------------------------------------------

  function getState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function setState(items) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  function isAdded(handle) {
    return getState().some(p => p.handle === handle);
  }

  function addProduct(product) {
    const state = getState();
    if (state.length >= MAX_ITEMS) return false;
    if (isAdded(product.handle)) return false;
    setState([...state, product]);
    return true;
  }

  function removeProduct(handle) {
    setState(getState().filter(p => p.handle !== handle));
  }

  // ----------------------------------------------------------------
  // Config (injected by theme.liquid)
  // ----------------------------------------------------------------

  const config = window.comparisonSettings || {};
  const excludeTags = (config.excludeTag || '')
    .split(',')
    .map(t => t.toLowerCase().trim())
    .filter(Boolean);

  function isExcluded(tagsArray) {
    if (!excludeTags.length) return false;
    return tagsArray.some(t => excludeTags.includes(t.toLowerCase().trim()));
  }

  // ----------------------------------------------------------------
  // Buttons
  // ----------------------------------------------------------------

  function updateAllButtons() {
    document.querySelectorAll('[data-comparison-btn]').forEach(btn => {
      const handle = btn.dataset.handle;
      const added = isAdded(handle);
      btn.classList.toggle('is-active', added);
      btn.setAttribute('aria-pressed', added);
      btn.textContent = added ? 'Remove' : 'Compare';
    });
  }

  function handleButtonClick(btn) {
    const handle = btn.dataset.handle;
    const title  = btn.dataset.title;
    const image  = btn.dataset.image;
    const url    = btn.dataset.url;

    if (isAdded(handle)) {
      removeProduct(handle);
    } else {
      const state = getState();
      if (state.length >= MAX_ITEMS) {
        showToast(`You can compare up to ${MAX_ITEMS} products at a time.`);
        return;
      }
      addProduct({ handle, title, image, url });
    }

    updateAllButtons();
    updateBar();
  }

  // ----------------------------------------------------------------
  // Toast
  // ----------------------------------------------------------------

  function showToast(message) {
    let toast = document.getElementById('comparison-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'comparison-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => toast.classList.remove('is-visible'), 3000);
  }

  // ----------------------------------------------------------------
  // Floating bar
  // ----------------------------------------------------------------

  function updateBar() {
    const bar = document.getElementById('comparison-bar');
    if (!bar) return;

    const state = getState();
    const countEl = bar.querySelector('[data-comparison-count]');
    const thumbsEl = bar.querySelector('[data-comparison-thumbs]');

    if (countEl) countEl.textContent = state.length;

    if (thumbsEl) {
      thumbsEl.innerHTML = state.map(p => `
        <div class="comparison-bar__thumb" data-handle="${p.handle}">
          ${p.image
            ? `<img src="${p.image}" alt="${p.title}" width="48" height="48">`
            : `<div class="comparison-bar__thumb-placeholder"></div>`
          }
          <button class="comparison-bar__remove" data-remove="${p.handle}" aria-label="Remove ${p.title}">
            &times;
          </button>
        </div>
      `).join('');
    }

    const hasItems = state.length > 0;
    bar.classList.toggle('is-visible', hasItems);
    if (!hasItems) setBarOpen(false);
  }

  function setBarOpen(open) {
    barOpen = open;
    const bar = document.getElementById('comparison-bar');
    if (!bar) return;
    bar.classList.toggle('is-expanded', open);
  }

  function toggleBar() {
    setBarOpen(!barOpen);
  }

  // ----------------------------------------------------------------
  // Modal
  // ----------------------------------------------------------------

  async function fetchProductData(handle) {
    const res = await fetch(`/products/${handle}?view=comparison`);
    if (!res.ok) throw new Error(`Failed to fetch ${handle}`);
    return res.json();
  }

  async function openModal() {
    const modal = document.getElementById('comparison-modal');
    if (!modal) return;

    const state = getState();
    const tableWrap = modal.querySelector('[data-comparison-table]');
    const searchWrap = modal.querySelector('[data-comparison-search-wrap]');

    tableWrap.innerHTML = '<p class="comparison-modal__loading">Loading&hellip;</p>';
    modal.classList.add('is-open');
    document.body.classList.add('comparison-modal-open');

    try {
      cachedProducts = await Promise.all(state.map(p => fetchProductData(p.handle)));
      renderTable(cachedProducts, tableWrap);
    } catch (err) {
      tableWrap.innerHTML = '<p class="comparison-modal__error">Something went wrong loading product data. Please try again.</p>';
      console.error('[Comparison] fetch error:', err);
    }

    if (searchWrap) renderSearch(searchWrap);
  }

  function closeModal() {
    const modal = document.getElementById('comparison-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('comparison-modal-open');
    clearSearch();
  }

  function renderTable(products, container) {
    if (!products.length) {
      container.innerHTML = '<p class="comparison-modal__empty">No products selected.</p>';
      return;
    }

    const rows = products[0]?.rows || [];
    let html = '<div class="comparison-table-wrap"><table class="comparison-table">';

    // Header row
    html += '<thead><tr><th class="comparison-table__label-cell"></th>';
    products.forEach(p => {
      html += `
        <th class="comparison-table__product-cell">
          <button class="comparison-table__remove" data-remove="${p.handle}" aria-label="Remove ${p.title}">&times;</button>
          ${p.image ? `<img src="${p.image}" alt="${p.title}" width="120" height="120">` : ''}
          <a href="${p.url}" class="comparison-table__product-title">${p.title}</a>
          <p class="comparison-table__product-price">${p.price}</p>
          <a href="${p.url}" class="comparison-table__product-cta">View product</a>
        </th>
      `;
    });

    const emptySlots = MAX_ITEMS - products.length;
    for (let i = 0; i < emptySlots; i++) {
      html += `
        <th class="comparison-table__product-cell comparison-table__product-cell--empty">
          <div class="comparison-table__empty-slot">
          </div>
        </th>
      `;
    }
    html += '</tr></thead>';

    // Body rows
    html += '<tbody>';
    rows.forEach((row, rowIndex) => {
      html += `<tr class="${rowIndex % 2 === 0 ? '' : 'is-alt'}">`;

      if (row.type === 'dropdown') {
        if (dropdownState[rowIndex] === undefined) dropdownState[rowIndex] = 0;
        const selectedIdx = dropdownState[rowIndex];

        html += `<td class="comparison-table__label-cell">
          <select class="comparison-table__dropdown" data-comparison-dropdown="${rowIndex}">
            ${row.options.map((opt, i) =>
              `<option value="${i}" ${i === selectedIdx ? 'selected' : ''}>${opt.label}</option>`
            ).join('')}
          </select>
        </td>`;

        products.forEach(p => {
          const productRow = p.rows[rowIndex];
          const opt = productRow?.options?.[selectedIdx];
          const value = opt?.value;
          const suffix = productRow?.suffix || '';
          const isEmpty = value === undefined || value === null || value === '' || value === 0 || value === '0';
          const display = isEmpty ? '—' : `${value}${suffix ? ' ' + suffix : ''}`;
          html += `<td class="comparison-table__data-cell">${display}</td>`;
        });
      } else {
        const labelText = row.title || row.options?.[0]?.label || '—';
        html += `<td class="comparison-table__label-cell">${labelText}</td>`;

        products.forEach(p => {
          const productRow = p.rows[rowIndex];
          const opt = productRow?.options?.[0];
          const value = opt?.value;
          const suffix = productRow?.suffix || '';
          const isEmpty = value === undefined || value === null || value === '' || value === 0 || value === '0';
          const display = isEmpty ? '—' : `${value}${suffix ? ' ' + suffix : ''}`;
          html += `<td class="comparison-table__data-cell">${display}</td>`;
        });
      }

      for (let i = 0; i < emptySlots; i++) {
        html += `<td class="comparison-table__data-cell comparison-table__data-cell--empty"></td>`;
      }

      html += '</tr>';
    });

    html += '</tbody></table></div>';
    container.innerHTML = html;
  }

  // ----------------------------------------------------------------
  // Search (inside modal)
  // ----------------------------------------------------------------

  let searchTimeout = null;

  function renderSearch(container) {
    container.innerHTML = `
      <div class="comparison-search">
        <input
          type="search"
          class="comparison-search__input"
          placeholder="Search to add a product&hellip;"
          aria-label="Search products to compare"
          id="comparison-search-input"
        >
        <ul class="comparison-search__results" id="comparison-search-results" role="listbox" aria-label="Search results"></ul>
      </div>
    `;
  }

  function clearSearch() {
    const input = document.getElementById('comparison-search-input');
    const results = document.getElementById('comparison-search-results');
    if (input) input.value = '';
    if (results) results.innerHTML = '';
  }

  async function runSearch(query) {
    const results = document.getElementById('comparison-search-results');
    if (!results) return;

    if (!query.trim()) {
      results.innerHTML = '';
      return;
    }

    results.innerHTML = '<li class="comparison-search__status">Searching&hellip;</li>';

    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );
      const data = await res.json();
      const suggestions = data?.resources?.results?.products || [];

      const state = getState();
      const filtered = suggestions.filter(p => {
        if (state.some(s => s.handle === p.handle)) return false;
        if (excludeTags.length && p.tags && isExcluded(p.tags)) return false;
        return true;
      });

      if (!filtered.length) {
        results.innerHTML = '<li class="comparison-search__status">No results found.</li>';
        return;
      }

      results.innerHTML = filtered.map(p => `
        <li class="comparison-search__result" role="option">
          <button class="comparison-search__result-btn" data-search-add
            data-handle="${p.handle}"
            data-title="${p.title}"
            data-image="${p.featured_image?.url || ''}"
            data-url="${p.url}">
            ${p.featured_image?.url
              ? `<img src="${p.featured_image.url}" alt="${p.title}" width="40" height="40">`
              : '<div class="comparison-search__result-placeholder"></div>'
            }
            <span>${p.title}</span>
          </button>
        </li>
      `).join('');

    } catch (err) {
      results.innerHTML = '<li class="comparison-search__status">Search failed. Please try again.</li>';
      console.error('[Comparison] search error:', err);
    }
  }

  // ----------------------------------------------------------------
  // Event delegation
  // ----------------------------------------------------------------

  document.addEventListener('click', async function (e) {

    //Comparison bar opener
    const opener = e.target.closest('.comparison-bar_opener');
    if (opener) {
      toggleBar();
      return;
    }
    
    // Compare button on card / product page
    const btn = e.target.closest('[data-comparison-btn]');
    if (btn) {
      handleButtonClick(btn);
      return;
    }

    // Remove from bar
    const barRemove = e.target.closest('[data-remove]');
    if (barRemove && barRemove.closest('#comparison-bar')) {
      removeProduct(barRemove.dataset.remove);
      updateAllButtons();
      updateBar();
      return;
    }

    // Open modal from bar
    const openBtn = e.target.closest('[data-comparison-open]');
    if (openBtn) {
      await openModal();
      return;
    }

    // Close modal — button or overlay
    if (e.target.closest('[data-comparison-close]') || e.target.id === 'comparison-modal') {
      closeModal();
      return;
    }

    // Remove from inside modal table
    const tableRemove = e.target.closest('.comparison-table__remove');
    if (tableRemove) {
      removeProduct(tableRemove.dataset.remove);
      updateAllButtons();
      updateBar();
      await openModal();
      return;
    }

    // Add from search results
    const searchAdd = e.target.closest('[data-search-add]');
    if (searchAdd) {
      const state = getState();
      if (state.length >= MAX_ITEMS) {
        showToast(`You can compare up to ${MAX_ITEMS} products at a time.`);
        return;
      }
      addProduct({
        handle: searchAdd.dataset.handle,
        title:  searchAdd.dataset.title,
        image:  searchAdd.dataset.image,
        url:    searchAdd.dataset.url
      });
      updateAllButtons();
      updateBar();
      await openModal();
      return;
    }

  });

  document.addEventListener('change', function (e) {
    const dropdown = e.target.closest('[data-comparison-dropdown]');
    if (!dropdown) return;

    const rowIndex = parseInt(dropdown.dataset.comparisonDropdown, 10);
    const selectedIdx = parseInt(dropdown.value, 10);
    dropdownState[rowIndex] = selectedIdx;

    const tableWrap = document.querySelector('[data-comparison-table]');
    if (tableWrap && cachedProducts.length) {
      renderTable(cachedProducts, tableWrap);
    }
  });

  // Search input — debounced
  document.addEventListener('input', function (e) {
    if (!e.target.closest('#comparison-search-input')) return;
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => runSearch(e.target.value), 300);
  });

  // Close modal on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  // ----------------------------------------------------------------
  // Init
  // ----------------------------------------------------------------

  function init() {
    updateAllButtons();
    updateBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();